import { escapeHTML } from '../../utils/escape.js';
import { formatMesLargo } from '../../utils/formatters.js';
import { calendarioDeMes, feriadosDelMes } from '../../shared/feriados-ui-policy.js';
import { renderCapEditor } from './LiquidacionView.js';

/**
 * Cuerpo del cajón "Configuración" del tablero (Unit S6, design.md AD7
 * extendido — un CUARTO `tableroDrawer.tipo` sobre el MISMO `DrawerShell`/
 * `#admin-liquidacion-drawer-mount` que Adelanto (S4) y Anomalías (S3), spec
 * `payroll-holidays-config`). Tres bloques, en el orden del mockup
 * aprobado: calendario de feriados, tope mensual (reusa `renderCapEditor`
 * SIN cambios — Req "Tope Mensual Editor": "hosted unchanged"), "Período
 * por defecto". Función de render pura: escapa todo valor interpolado ella
 * misma (AD7 — el shell sólo escapa el título).
 *
 * Deviation from design.md's File Changes table (documented in
 * apply-progress.md): design.md names this file `FeriadosCalendarView.js`
 * as a standalone body limited to the calendar + período selector. This
 * unit implements the broader `ConfiguracionLiquidacionView.js` per the
 * orchestrator's explicit mockup description — one configuration area
 * hosting feriados + the reused tope editor + período por defecto, opened
 * from a NEW toolbar "Configuración" button rather than the legacy gear
 * toggle already living in `LiquidacionView.js`'s own `#admin-liquidacion-mount`
 * (which keeps existing unchanged, per AD1 — this is an ADDITIONAL entry
 * point, not a replacement).
 */

const NAV_BTN =
    'px-2 py-1 rounded-lg text-xs font-black text-text-muted hover:text-text-primary focus:outline-none focus:border-brand border border-border-subtle';
const DIA_BASE = 'min-h-[60px] rounded-lg border p-1 text-[11px] text-left w-full';
const DIA_VACIO = `${DIA_BASE} border-transparent opacity-30 pointer-events-none`;
// Bug reportado por el owner (2026-09-05): `focus:border-brand` pintaba el
// mismo borde verde que `DIA_SELECCIONADO` — un día enfocado (por foco de
// teclado, o por el bug de foco corregido en `capturarFocoDrawerRepintado`)
// se veía indistinguible de un día realmente seleccionado. `focus-visible`
// sólo aplica con navegación por teclado (nunca en un click de mouse), así
// que el estilo de foco deja de leerse como selección en ningún caso.
const DIA_LIBRE = `${DIA_BASE} border-border-subtle hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand`;
const DIA_FERIADO = `${DIA_BASE} border-amber-300 bg-amber-50`;
const DIA_SELECCIONADO = `${DIA_BASE} border-brand bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand`;

const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

/**
 * `'8 de agosto'` — reusa {@link formatMesLargo} (le descarta el año) para no
 * duplicar la lista de nombres de mes.
 * @param {string} fecha `'YYYY-MM-DD'`
 * @returns {string}
 */
function formatFechaLarga(fecha) {
    const [, , diaStr] = (fecha ?? '').split('-');
    const dia = Number(diaStr);
    const nombreMes = formatMesLargo((fecha ?? '').slice(0, 7)).split(' ')[0];
    if (!dia || !nombreMes) return fecha ?? '—';
    return `${dia} de ${nombreMes}`;
}

/**
 * Celda de día: SIEMPRE un botón seleccionable (nunca aloja el form de alta
 * — ese vive AFUERA de la grilla, ver {@link renderFeriadoForm} — la grilla
 * de 7 columnas es demasiado angosta para un input + 2 botones sin
 * desbordar). El día seleccionado (`draftFecha === celda.fecha`) se marca
 * con un estilo distinto y `aria-pressed`, como cualquier toggle button.
 * @param {{fecha:string,dia:number,enMes:boolean}} celda
 * @param {Map<string,{fecha:string,descripcion:string}>} feriadoPorFecha
 * @param {string|null} draftFecha
 * @param {string|null} quitandoFecha
 * @param {boolean} cargando review-1fa036d7d40a06c8 (finding 1): mientras la
 * lista carga, "Agregar"/"Quitar" quedan deshabilitados — reflejo visual del
 * mismo guard que ya aplican los handlers en `js/admin.js`.
 * @returns {string}
 */
function renderDiaCelda(celda, feriadoPorFecha, draftFecha, quitandoFecha, cargando) {
    if (!celda.enMes) {
        return `<div class="${DIA_VACIO}"><span class="block font-bold">${celda.dia}</span></div>`;
    }

    const feriado = feriadoPorFecha.get(celda.fecha);
    if (feriado) {
        const quitando = quitandoFecha === celda.fecha;
        return `
            <div class="${DIA_FERIADO}">
                <span class="block font-bold text-text-primary">${celda.dia}</span>
                <span class="block text-[10px] text-amber-900 truncate" title="${escapeHTML(feriado.descripcion)}">${escapeHTML(feriado.descripcion)}</span>
                <button type="button" data-action="config-feriado-quitar" data-fecha="${escapeHTML(celda.fecha)}" ${quitando || cargando ? 'disabled' : ''} class="mt-1 w-full rounded border border-red-300 text-red-700 text-[9px] font-black uppercase py-0.5">Quitar</button>
            </div>
        `;
    }

    const seleccionado = draftFecha === celda.fecha;
    return `
        <button type="button" data-action="config-feriado-abrir" data-fecha="${escapeHTML(celda.fecha)}" aria-pressed="${seleccionado ? 'true' : 'false'}" ${cargando ? 'disabled' : ''} class="${seleccionado ? DIA_SELECCIONADO : DIA_LIBRE}">
            <span class="block font-bold text-text-primary">${celda.dia}</span>
        </button>
    `;
}

/**
 * Form de alta, renderizado UNA vez debajo de toda la grilla (nunca dentro
 * de una celda — ver {@link renderDiaCelda}). Mismos atributos
 * (`data-action-form`, `data-fecha`, `name`, `id`) que antes, así
 * `js/admin.js` (capture/apply del draft, el submit handler) y los tests
 * existentes siguen sin cambios.
 * @param {{fecha:string,descripcion:string,error:string|null}|null} draft
 * @param {boolean} submitting
 * @returns {string}
 */
function renderFeriadoForm(draft, submitting) {
    if (!draft) return '';
    return `
        <div class="mt-4 pt-4 border-t border-border-subtle">
            <form data-action-form="config-feriado-agregar" data-fecha="${escapeHTML(draft.fecha)}" class="flex flex-wrap items-end gap-3">
                <h4 class="w-full text-xs font-black text-text-primary">Agregar feriado el ${escapeHTML(formatFechaLarga(draft.fecha))}</h4>
                <div class="flex flex-1 min-w-[180px] flex-col gap-1">
                    <label for="config-feriado-descripcion" class="text-[10px] font-black uppercase text-text-muted">Descripción</label>
                    <input id="config-feriado-descripcion" type="text" name="descripcion" value="${escapeHTML(draft.descripcion ?? '')}" placeholder="Descripción del feriado" class="w-full rounded-xl border border-border-subtle px-3 py-2 text-xs focus:outline-none focus:border-brand" />
                    ${draft.error ? `<span role="alert" class="text-[11px] font-bold text-red-700">${escapeHTML(draft.error)}</span>` : ''}
                </div>
                <div class="flex gap-2">
                    <button type="submit" ${submitting ? 'disabled' : ''} class="px-4 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase disabled:opacity-60">Agregar</button>
                    <button type="button" data-action="config-feriado-cancelar" ${submitting ? 'disabled' : ''} class="px-4 py-2 rounded-xl border border-border-subtle text-[11px] font-black uppercase disabled:opacity-60">Cancelar</button>
                </div>
            </form>
        </div>
    `;
}

/**
 * @param {{mes:string, feriadosCargando:boolean, feriadosError:string|null, feriados:Array<object>, feriadoDraft:object|null, feriadoSubmitting:boolean, quitandoFecha:string|null}} model
 * @returns {string}
 */
function renderCalendarioFeriados(model) {
    const semanas = calendarioDeMes(model.mes) ?? [];
    const delMes = feriadosDelMes(model.feriados ?? [], model.mes);
    const feriadoPorFecha = new Map(delMes.map((f) => [f.fecha, f]));

    const estadoHtml = model.feriadosCargando
        ? '<p class="text-xs text-text-muted mb-3">Cargando feriados...</p>'
        : model.feriadosError
          ? `<p role="alert" class="text-xs font-bold text-red-700 mb-3">${escapeHTML(model.feriadosError)}</p>`
          : '';

    const headerDias = DIAS_SEMANA.map((d) => `<span class="text-center text-[10px] font-black uppercase text-text-muted">${d}</span>`).join('');
    const filas = semanas
        .map(
            (semana) => `
                <div class="grid grid-cols-7 gap-1">
                    ${semana.map((celda) => renderDiaCelda(celda, feriadoPorFecha, model.feriadoDraft?.fecha ?? null, model.quitandoFecha, model.feriadosCargando)).join('')}
                </div>
            `,
        )
        .join('');

    return `
        <div class="bg-surface border border-border-subtle rounded-2xl p-6 mb-8">
            <div class="flex items-center justify-between mb-3">
                <h3 class="text-xs font-black uppercase tracking-widest text-text-muted">Feriados</h3>
                <div class="flex items-center gap-2">
                    <button type="button" data-action="config-mes-anterior" aria-label="Mes anterior" class="${NAV_BTN}">‹</button>
                    <span class="text-xs font-bold text-text-primary capitalize">${escapeHTML(formatMesLargo(model.mes))}</span>
                    <button type="button" data-action="config-mes-siguiente" aria-label="Mes siguiente" class="${NAV_BTN}">›</button>
                </div>
            </div>
            ${estadoHtml}
            <div class="grid grid-cols-7 gap-1 mb-1">${headerDias}</div>
            <div class="flex flex-col gap-1">${filas}</div>
            ${renderFeriadoForm(model.feriadoDraft, model.feriadoSubmitting)}
        </div>
    `;
}

/**
 * @param {{value:string, saving:boolean, error:string|null}} model
 * @returns {string}
 */
function renderPeriodoPorDefecto({ value, saving, error }) {
    const opciones = [
        ['mes', 'Por mes'],
        ['quincena', 'Por quincena'],
        ['semana', 'Por semana'],
    ]
        .map(([v, label]) => `<option value="${v}" ${v === value ? 'selected' : ''}>${label}</option>`)
        .join('');

    return `
        <div class="bg-surface border border-border-subtle rounded-2xl p-6">
            <h3 class="text-xs font-black uppercase tracking-widest text-text-muted mb-3">Período por defecto</h3>
            ${error ? `<p role="alert" class="text-xs font-bold text-red-700 mb-3">${escapeHTML(error)}</p>` : ''}
            <form data-action-form="config-periodo-por-defecto" class="flex items-center gap-3">
                <select name="periodo_por_defecto" class="bg-base border border-border-subtle rounded-xl px-3 py-2 text-xs text-text-primary focus:outline-none focus:border-brand">
                    ${opciones}
                </select>
                <button type="submit" ${saving ? 'disabled' : ''} class="px-4 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow-lg disabled:opacity-60">Guardar</button>
            </form>
        </div>
    `;
}

/**
 * @param {{
 *   mes: string,
 *   feriadosCargando: boolean,
 *   feriadosError: string|null,
 *   feriados: Array<{fecha:string,descripcion:string}>,
 *   feriadoDraft: {fecha:string,descripcion:string,error:string|null}|null,
 *   feriadoSubmitting: boolean,
 *   quitandoFecha: string|null,
 *   capConfig: object|null,
 *   periodoPorDefecto: string,
 *   periodoPorDefectoSaving: boolean,
 *   periodoPorDefectoError: string|null,
 * }} [model]
 * @returns {string}
 */
export function ConfiguracionLiquidacionView(model = {}) {
    return `
        <div>
            ${renderCalendarioFeriados(model)}
            ${renderCapEditor(model.capConfig)}
            ${renderPeriodoPorDefecto({
                value: model.periodoPorDefecto ?? 'mes',
                saving: Boolean(model.periodoPorDefectoSaving),
                error: model.periodoPorDefectoError ?? null,
            })}
        </div>
    `;
}
